import { useEffect, useContext, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Modify from 'ol/interaction/Modify';
import { Collection } from 'ol';
import { Style, Fill, Stroke } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import MapContext from '../Map/MapContext';
import { useToast } from '../../context/ToastContext';
import { updateIncidenceGeometry } from '../../services/incidenceService';

/**
 * Componente para editar la geometría de polígonos.
 * Maneja su propia interacción Modify y estado.
 */
const EditGeometry = ({ feature, layer, onEditEnd }) => {
    const { t } = useTranslation();
    const { map } = useContext(MapContext);
    const { addToast } = useToast();

    const modifyRef = useRef(null);
    const originalStyleRef = useRef(null);
    const initializedRef = useRef(false);

    // Iniciar edición cuando se proporciona feature y layer
    useEffect(() => {
        if (!map || !feature || !layer || initializedRef.current) return;

        initializedRef.current = true;

        // Guardar el estilo original
        originalStyleRef.current = feature.getStyle();

        // Aplicar estilo de edición (color amarillo/ocre, línea discontinua)
        const editingStyle = new Style({
            stroke: new Stroke({
                color: '#f7c80c',
                width: 6,
                lineDash: [15, 10], // Línea discontinua con más separación
            }),
            fill: new Fill({
                color: 'rgba(212, 165, 116, 0.4)',
            }),
        });
        feature.setStyle(editingStyle);

        // Esperar un momento para que el modal se cierre
        const timer = setTimeout(() => {
            const modify = new Modify({
                features: new Collection([feature])
            });

            map.addInteraction(modify);
            modifyRef.current = modify;

            const mapElement = map.getTargetElement();
            if (mapElement) mapElement.style.cursor = 'pointer';

            // Actualizar la capa cuando se modifique
            modify.on('modifyend', () => {
                layer.getSource().changed();
            });
        }, 100);

        return () => {
            clearTimeout(timer);
        };
    }, [map, feature, layer]);

    // Función para finalizar edición
    const stopEdit = async () => {
        if (!modifyRef.current || !map) return;

        map.removeInteraction(modifyRef.current);
        modifyRef.current = null;

        const currentFeature = feature;
        const currentLayer = layer;

        // Guardar geometría en el backend usando el endpoint específico
        let geometrySaved = false;
        if (currentFeature && currentLayer) {
            const incidenceId = currentFeature.get('id');
            
            if (incidenceId) {
                try {
                    const geometry = currentFeature.getGeometry();
                    if (geometry) {
                        const geoJSONFormat = new GeoJSON();
                        // writeGeometry devuelve un objeto GeoJSON directamente
                        const geoJSONGeometry = geoJSONFormat.writeGeometry(geometry, {
                            dataProjection: 'EPSG:4326',
                            featureProjection: map.getView().getProjection()
                        });

                        // El backend espera 'array' (objeto JSON válido en Laravel)
                        // Asegurémonos de que sea un objeto plano serializable
                        let geometryObj;
                        if (typeof geoJSONGeometry === 'string') {
                            geometryObj = JSON.parse(geoJSONGeometry);
                        } else {
                            // Asegurar que sea un objeto plano (clonar para evitar referencias)
                            geometryObj = JSON.parse(JSON.stringify(geoJSONGeometry));
                        }

                        // Usar el endpoint específico para actualizar solo la geometría
                        const response = await updateIncidenceGeometry(incidenceId, {
                            geometry: geometryObj
                        });

                        // Intentar parsear la respuesta, pero si falla (error 500), asumir que la geometría se guardó
                        let responseData = null;
                        try {
                            responseData = await response.json();
                        } catch (e) {
                            // Si no se puede parsear (error 500), la geometría probablemente se guardó antes del error
                            // updateGeometryFromGeoJSON se ejecuta antes de recalculateLocation
                            console.warn('No se pudo parsear la respuesta, pero la geometría probablemente se guardó:', e);
                            geometrySaved = true;
                        }
                        
                        // La geometría se guarda antes de recalcular la location
                        // Si response.ok es false, puede ser porque falló el recálculo de location
                        // pero la geometría ya se guardó correctamente
                        if (response.ok || (responseData && responseData.success)) {
                            geometrySaved = true;
                        } else if (response.status === 500) {
                            // Error 500 probablemente significa que falló recalculateLocation pero la geometría se guardó
                            geometrySaved = true;
                            console.warn('Geometría guardada pero falló el recálculo de location');
                        } else {
                            // Solo lanzar error si realmente falló el guardado
                            throw new Error(responseData?.message || 'Error al guardar la geometría');
                        }
                    }
                } catch (error) {
                    console.error('Error al guardar la geometría:', error);
                    // Solo mostrar error si realmente no se guardó
                    if (!geometrySaved) {
                        addToast({
                            id: 'geometry-edit-error',
                            show: true,
                            title: t('Error'),
                            message: t('Error al guardar la geometría modificada'),
                            duration: 5000,
                            variations: 'danger'
                        });
                    }
                }
            }
        }

        // Restaurar estilo original
        if (currentFeature && originalStyleRef.current !== undefined) {
            currentFeature.setStyle(originalStyleRef.current);
        }

        // Actualizar capa
        if (currentLayer) {
            currentLayer.getSource().changed();
        }

        // Notificación de éxito solo si se guardó correctamente
        if (geometrySaved) {
            addToast({
                id: 'geometry-edit-success',
                show: true,
                title: t('Geometría modificada'),
                message: t('Se ha modificado correctamente la geometría'),
                duration: 3000,
                variations: 'light'
            });
        }

        originalStyleRef.current = null;
        initializedRef.current = false;

        const mapElement = map.getTargetElement();
        if (mapElement) mapElement.style.cursor = '';

        // Callback cuando finaliza
        if (onEditEnd) {
            onEditEnd();
        }
    };

    // Limpiar al desmontar o cuando cambian feature/layer
    useEffect(() => {
        return () => {
            if (modifyRef.current && map) {
                map.removeInteraction(modifyRef.current);
                modifyRef.current = null;
            }
            if (feature && originalStyleRef.current !== undefined) {
                feature.setStyle(originalStyleRef.current);
                originalStyleRef.current = null;
            }
            initializedRef.current = false;
        };
    }, [map, feature]);

    if (!feature || !layer) return null;

    return (
        <div 
            style={{ 
                position: 'fixed', 
                bottom: 20, 
                right: 20, 
                zIndex: 9999,
                background: '#fff',
                border: '2px solid #dc3545',
                borderRadius: '8px',
                padding: '12px 16px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}
        >
            <div className="d-flex align-items-center gap-2">
                <i className="bi bi-pencil-square text-danger"></i>
                <span className="fw-bold">{t("Editando geometría")}</span>
                <button
                    type="button"
                    className="btn btn-danger btn-sm ms-2"
                    onClick={stopEdit}
                >
                    <i className="bi bi-x-circle me-1"></i>
                    {t("Finalizar")}
                </button>
            </div>
        </div>
    );
};

export default EditGeometry;

